import { useEffect, useMemo, useState } from 'react';
import type { AiArtifact, AiReport, AiRun, IdeaDetailResponse } from '@shared/types';
import { ARTIFACT_KIND_HINTS, ARTIFACT_KIND_LABELS } from '@shared/ai';
import {
  AI_CONFIDENCE_LABELS,
  AI_REPORT_READONLY_NOTE,
  AI_RUN_STATUS_LABELS,
  AI_VERDICT_DESCRIPTIONS,
  aiScoreBand,
} from '@shared/ideaStatus';
import { formatDateTime, relativeTime, truncate } from '@shared/format';
import { Badge, QualityBar } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner } from '../../../components/Card';
import { VerdictBadge } from './ideaBadges';
import { Segmented, TabBar } from '../../../components/Tabs';
import { toast } from '../../../components/Toast';

/**
 * AI 研判面板。
 *
 * 三态是显式的：排队中 / 研判中 / 已完成，失败也如实显示错误与重试入口。
 * 面板本身只读 —— 结论与产物不可人工编辑，否则「AI 说的」就失去意义了。
 */

const RISK_TONES = { high: 'critical', medium: 'caution', low: 'neutral' } as const;

function RunStrip({ run }: { run: AiRun }) {
  const active = run.status === 'queued' || run.status === 'running';
  return (
    <div className="flex items-center gap-2 rounded-md border border-line bg-surface-2 px-3 py-2 text-xs">
      {active ? <Spinner className="size-3.5 text-accent" /> : null}
      <span className={active ? 'text-accent' : 'text-fg-muted'}>
        {AI_RUN_STATUS_LABELS[run.status] ?? run.status}
        {run.status === 'queued' ? '（模型调用通常需要几十秒）' : ''}
      </span>
      <span className="ml-auto text-[10px] text-fg-subtle" title={run.createdAt}>
        {formatDateTime(run.createdAt)}
      </span>
    </div>
  );
}

/**
 * 模型的推理过程。
 *
 * 推理型模型（DeepSeek-V4 这类）会把思考过程单独返回，这是判断结论可信度的关键材料：
 * 能看到它读了哪些信息、在哪一步犹豫，比只看一个分数有用得多。
 *
 * 默认折叠：思考过程通常比结论长一个数量级，摊开会把结论挤到屏幕外。
 * 同时明确标注「这不是结论」，避免有人把中间的推测当成最终判断。
 */
function ReasoningTrace({ run }: { run: AiRun }) {
  const [open, setOpen] = useState(false);
  if (run.reasoningExcerpt === '') return null;

  return (
    <section className="flex flex-col gap-2 rounded-md border border-line bg-surface-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 px-3 py-2 text-left text-[11px] text-fg-muted hover:text-fg"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        <span className="font-medium">模型的推理过程</span>
        <span className="text-fg-subtle">
          （{run.reasoningExcerpt.length} 字
          {run.reasoningTokens !== null ? ` · 消耗 ${run.reasoningTokens} 推理 token` : ''}
          ，仅供参考，不是结论）
        </span>
      </button>
      {open ? (
        <div className="border-t border-line px-3 py-2.5">
          <pre className="idea-body m-0 text-[11px] leading-relaxed whitespace-pre-wrap text-fg-subtle">
            {run.reasoningExcerpt}
          </pre>
          <p className="mt-2 text-[10px] text-fg-subtle">
            这是模型自己的思考记录，可能包含被它自己推翻的中间判断；只保留前 4000 字。
          </p>
        </div>
      ) : null}
    </section>
  );
}

function ArtifactViewer({ artifact }: { artifact: AiArtifact }) {
  const isCode = artifact.kind === 'code';
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] leading-relaxed text-fg-subtle">{ARTIFACT_KIND_HINTS[artifact.kind]}</p>
      <div className="rounded-md border border-line bg-surface-2 p-3">
        <pre className={['m-0 text-xs leading-relaxed text-fg', isCode ? 'code-block' : 'idea-body whitespace-pre-wrap'].join(' ')}>
          {artifact.content}
        </pre>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="default"
          onClick={() => {
            void navigator.clipboard
              .writeText(artifact.content)
              .then(() => toast.ok('已复制到剪贴板'))
              .catch(() => toast.caution('复制失败', '浏览器拒绝了剪贴板访问，请手动选择文本复制。'));
          }}
        >
          复制
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            const blob = new Blob([artifact.content], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${artifact.kind}.md`;
            anchor.click();
            URL.revokeObjectURL(url);
          }}
        >
          下载 .md
        </Button>
      </div>
    </div>
  );
}

function ReportBody({ report }: { report: AiReport }) {
  const band = aiScoreBand(report.score);
  const [activeKind, setActiveKind] = useState(report.artifacts[0]?.kind ?? 'experiment');

  const activeArtifact = useMemo(
    () => report.artifacts.find((artifact) => artifact.kind === activeKind) ?? report.artifacts[0] ?? null,
    [report.artifacts, activeKind],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <VerdictBadge verdict={report.verdict} score={report.score} />
        <Badge tone={band.tone}>{band.label}</Badge>
        <span className="ml-auto text-[10px] text-fg-subtle">
          {AI_CONFIDENCE_LABELS[report.confidence]} · {report.model} · {relativeTime(report.createdAt)}
        </span>
      </div>

      <p className="idea-body rounded-md border border-line bg-surface-2 p-3 text-xs leading-relaxed text-fg">
        {report.summary}
      </p>

      <p className="text-[11px] leading-relaxed text-fg-subtle">{AI_VERDICT_DESCRIPTIONS[report.verdict]}</p>

      <section className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold text-fg">五维评分</h4>
        <ul className="flex flex-col gap-2">
          {report.dimensions.map((dimension) => (
            <li key={dimension.key} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-2 text-[11px]">
                <span className="text-fg-muted">{dimension.label}</span>
                <span className="tabular text-fg">{dimension.score}</span>
              </div>
              <QualityBar value={dimension.score} tone={dimension.score >= 60 ? 'ok' : dimension.score >= 40 ? 'caution' : 'critical'} ariaLabel={dimension.label} />
              {dimension.note ? <p className="text-[10px] leading-relaxed text-fg-subtle">{dimension.note}</p> : null}
            </li>
          ))}
        </ul>
      </section>

      {report.risks.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-fg">风险与规避</h4>
          <ul className="flex flex-col gap-2">
            {report.risks.map((risk, index) => (
              <li key={`${risk.title}-${index}`} className="rounded-md border border-line bg-surface-2 p-2.5">
                <div className="flex items-center gap-2">
                  <Badge tone={RISK_TONES[risk.severity]}>{risk.severity === 'high' ? '高风险' : risk.severity === 'medium' ? '中风险' : '低风险'}</Badge>
                  <span className="text-xs font-medium text-fg">{risk.title}</span>
                </div>
                {risk.detail ? <p className="mt-1.5 text-[11px] leading-relaxed text-fg-muted">成因：{risk.detail}</p> : null}
                {risk.mitigation ? <p className="mt-1 text-[11px] leading-relaxed text-fg-muted">规避：{risk.mitigation}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {report.reusableAssets.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-fg">可复用资产</h4>
          <ul className="flex flex-col gap-1.5">
            {report.reusableAssets.map((asset, index) => (
              <li key={`${asset.name}-${index}`} className="flex items-baseline gap-2 text-[11px]">
                <span className="font-medium text-fg">{asset.name}</span>
                <Badge tone="neutral">{asset.kind}</Badge>
                <span className="min-w-0 flex-1 text-fg-muted">{asset.note}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <p className="text-[11px] text-fg-subtle">模型没有给出可复用资产 —— 要么确实没有可复用的，要么团队能力清单还没填。</p>
      )}

      {report.mvpSteps.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-fg">最小验证拆解</h4>
          <ol className="flex flex-col gap-2">
            {report.mvpSteps.map((step) => (
              <li key={`${step.order}-${step.title}`} className="flex gap-2.5">
                <span className="tabular mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-3 text-[10px] font-semibold text-fg-muted">
                  {step.order}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-fg">
                    {step.title}
                    <Badge tone="neutral" className="ml-1.5">
                      投入 {step.effort}
                    </Badge>
                  </p>
                  {step.detail ? <p className="mt-0.5 text-[11px] leading-relaxed text-fg-muted">{step.detail}</p> : null}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {report.openQuestions.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-fg">需要人来回答的问题</h4>
          <ul className="flex list-disc flex-col gap-1 pl-4 text-[11px] leading-relaxed text-fg-muted">
            {report.openQuestions.map((question, index) => (
              <li key={`${index}-${truncate(question, 20)}`}>{question}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {report.artifacts.length > 0 ? (
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-xs font-semibold text-fg">产物草稿</h4>
            <Segmented
              size="sm"
              value={activeKind}
              onChange={(value) => setActiveKind(value)}
              options={report.artifacts.map((artifact) => ({ value: artifact.kind, label: ARTIFACT_KIND_LABELS[artifact.kind] }))}
            />
          </div>
          {activeArtifact ? <ArtifactViewer artifact={activeArtifact} /> : null}
        </section>
      ) : (
        <p className="text-[11px] text-fg-subtle">这一轮没有产出可评审的草稿，可以在补充信息后重新研判。</p>
      )}

      <p className="border-t border-line pt-2 text-[10px] leading-relaxed text-fg-subtle">{AI_REPORT_READONLY_NOTE}</p>
    </div>
  );
}

export function AiReportPanel({
  idea,
  canRun,
  aiEnabled,
  aiConfigured,
  running,
  onRun,
}: {
  idea: IdeaDetailResponse;
  canRun: boolean;
  aiEnabled: boolean;
  aiConfigured: boolean;
  /** 由父组件维护的「刚刚发起」状态，避免等下一次刷新才看到进度 */
  running: boolean;
  onRun: () => void;
}) {
  const reports = idea.reports;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // 新结论落地后回到最新版本，否则用户会盯着上一轮的报告以为没变化
  const latestId = idea.latestReport?.id ?? null;
  useEffect(() => {
    setSelectedId(null);
  }, [latestId]);

  const selected = useMemo(() => {
    if (selectedId) return reports.find((report) => report.id === selectedId) ?? idea.latestReport;
    return idea.latestReport;
  }, [reports, selectedId, idea.latestReport]);

  const latestRun = idea.latestRun;
  const activeRun = running || latestRun?.status === 'queued' || latestRun?.status === 'running';

  /**
   * 推理过程属于**某一次具体的 run**，切换历史版本时必须跟着版本走 ——
   * 否则界面会把另一轮的思考过程当成这一轮结论的依据，而这恰恰是这个面板要保证的可追溯性。
   *
   * 例外：最新一次是失败或进行中的 run 时，就用它的思考过程，
   * 因为「为什么现在还没有新结论」的答案在那儿。
   */
  const traceRun = useMemo(() => {
    if (latestRun && latestRun.status !== 'succeeded') return latestRun;
    if (selected) return idea.runs.find((run) => run.reportId === selected.id) ?? latestRun;
    return latestRun;
  }, [idea.runs, selected, latestRun]);

  return (
    <Card>
      <CardHeader
        actions={
          canRun ? (
            <Button size="sm" variant="primary" loading={activeRun} disabled={!aiEnabled || !aiConfigured || activeRun} onClick={onRun}>
              {reports.length > 0 ? '重新研判' : '发起 AI 研判'}
            </Button>
          ) : null
        }
      >
        <CardTitle subtitle="基于创意信息与团队能力清单生成，结论可被推翻">
          AI 研判
          {reports.length > 1 ? ` · 共 ${reports.length} 个版本` : ''}
        </CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        {!aiConfigured ? (
          <p className="rounded-md border border-caution/50 bg-caution-soft px-3 py-2 text-[11px] leading-relaxed text-caution">
            尚未配置模型：需要在 Worker 里注入 <code className="font-mono">AI_API_KEY</code> 与{' '}
            <code className="font-mono">AI_BASE_URL</code> 后重新部署，才能发起研判。
          </p>
        ) : !aiEnabled ? (
          <p className="rounded-md border border-caution/50 bg-caution-soft px-3 py-2 text-[11px] leading-relaxed text-caution">
            管理员已在「系统设置」中关闭 AI 研判。
          </p>
        ) : null}

        {activeRun && latestRun ? <RunStrip run={{ ...latestRun, status: running ? 'queued' : latestRun.status }} /> : null}

        {latestRun?.status === 'failed' ? (
          <div className="rounded-md border border-critical/50 bg-critical-soft px-3 py-2 text-[11px] leading-relaxed text-critical">
            上一轮研判失败：{latestRun.error ?? '未知原因'}
          </div>
        ) : null}

        {idea.reportStale && selected ? (
          <p className="rounded-md border border-caution/50 bg-caution-soft px-3 py-2 text-[11px] leading-relaxed text-caution">
            创意内容在这份结论之后被修改过，结论可能已经过时，建议重新研判。
          </p>
        ) : null}

        {reports.length > 1 ? (
          <TabBar
            size="md"
            value={selected?.id ?? ''}
            onChange={(value) => setSelectedId(value)}
            items={reports.map((report, index) => ({
              key: report.id,
              label: index === 0 ? '最新结论' : `第 ${reports.length - index} 版`,
              hint: formatDateTime(report.createdAt),
            }))}
          />
        ) : null}

        {selected ? (
          <ReportBody report={selected} />
        ) : (
          <EmptyState
            title="还没有研判结论"
            description="发起研判后，模型会给出可行性评分、风险清单、可复用资产、最小验证拆解，以及 PRD / 接口 / 数据模型 / 代码片段草稿。"
          />
        )}

        {traceRun ? <ReasoningTrace run={traceRun} /> : null}
      </CardBody>
    </Card>
  );
}
